import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { 
  getMeetings, getMeeting, createMeeting, updateMeeting,
  startMeeting, completeMeeting, cancelMeeting,
  addMeetingAttendee, removeMeetingAttendee,
  addMeetingActionItem, updateMeetingActionItem,
  getUsers
} from '@/services/api';
import { 
  Calendar, Clock, MapPin, Users, Plus, Search,
  Play, CheckCircle2, XCircle, Eye, FileText,
  ListTodo, User, ChevronRight, AlertCircle, Loader2
} from 'lucide-react';

const MEETING_TYPES = [
  { value: 'safety_review', label: 'Safety Review Board', labelAr: 'مجلس مراجعة السلامة' },
  { value: 'sms_committee', label: 'SMS Committee', labelAr: 'لجنة نظام إدارة السلامة' },
  { value: 'incident_review', label: 'Incident Review', labelAr: 'مراجعة الحوادث' },
  { value: 'risk_review', label: 'Risk Review', labelAr: 'مراجعة المخاطر' },
  { value: 'training_review', label: 'Training Review', labelAr: 'مراجعة التدريب' },
  { value: 'emergency_briefing', label: 'Emergency Briefing', labelAr: 'إحاطة الطوارئ' },
  { value: 'other', label: 'Other', labelAr: 'أخرى' }
];

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', labelAr: 'مجدول', color: 'bg-blue-600', icon: Calendar },
  in_progress: { label: 'In Progress', labelAr: 'جاري', color: 'bg-amber-600', icon: Play },
  completed: { label: 'Completed', labelAr: 'مكتمل', color: 'bg-emerald-600', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', labelAr: 'ملغي', color: 'bg-slate-600', icon: XCircle }
};

export default function MeetingsPage() {
  const { t, i18n } = useTranslation();
  const { api, user } = useAuth();
  const { isRTL } = useLanguage();
  
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showMinutesDialog, setShowMinutesDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    meeting_type: 'safety_review',
    description: '',
    scheduled_date: '',
    scheduled_time: '09:00',
    location: '',
    duration_minutes: 60
  });
  
  const [minutesText, setMinutesText] = useState('');
  const [actionItemForm, setActionItemForm] = useState({
    description: '',
    assigned_to: '',
    due_date: ''
  });

  const canManageMeetings = ['admin', 'safety_manager', 'safety_officer'].includes(user?.role);

  useEffect(() => {
    if (api) {
      fetchMeetings();
      fetchUsers();
    }
  }, [api]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await getMeetings(api);
      setMeetings(response.data);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers(api);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      
      await createMeeting(api, {
        title: formData.title,
        title_ar: formData.title_ar || null,
        meeting_type: formData.meeting_type,
        description: formData.description || null,
        scheduled_date: scheduledDateTime.toISOString(),
        location: formData.location,
        duration_minutes: parseInt(formData.duration_minutes),
        agenda_items: [],
        invitees: []
      });
      
      setShowCreateDialog(false);
      resetForm();
      fetchMeetings();
    } catch (error) {
      console.error('Failed to create meeting:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStart = async (meeting) => {
    try {
      await startMeeting(api, meeting.id);
      fetchMeetings();
      if (selectedMeeting?.id === meeting.id) {
        const updated = await getMeeting(api, meeting.id);
        setSelectedMeeting(updated.data);
      }
    } catch (error) {
      console.error('Failed to start meeting:', error);
    }
  };

  const handleComplete = async () => {
    try {
      setSaving(true);
      await completeMeeting(api, selectedMeeting.id, { 
        minutes: minutesText || undefined 
      });
      setShowMinutesDialog(false);
      setMinutesText('');
      fetchMeetings();
      const updated = await getMeeting(api, selectedMeeting.id);
      setSelectedMeeting(updated.data);
    } catch (error) {
      console.error('Failed to complete meeting:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (meeting) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) return;
    try {
      await cancelMeeting(api, meeting.id);
      fetchMeetings();
      if (selectedMeeting?.id === meeting.id) {
        const updated = await getMeeting(api, meeting.id);
        setSelectedMeeting(updated.data);
      }
    } catch (error) {
      console.error('Failed to cancel meeting:', error);
    }
  };

  const handleAddAttendee = async (userId) => {
    if (!userId || !selectedMeeting) return;
    try {
      await addMeetingAttendee(api, selectedMeeting.id, userId);
      const updated = await getMeeting(api, selectedMeeting.id);
      setSelectedMeeting(updated.data);
    } catch (error) {
      console.error('Failed to add attendee:', error);
    }
  };

  const handleRemoveAttendee = async (userId) => {
    if (!selectedMeeting) return;
    try {
      await removeMeetingAttendee(api, selectedMeeting.id, userId);
      const updated = await getMeeting(api, selectedMeeting.id);
      setSelectedMeeting(updated.data);
    } catch (error) {
      console.error('Failed to remove attendee:', error);
    }
  };

  const handleAddActionItem = async () => {
    if (!actionItemForm.description || !selectedMeeting) return;
    try {
      await addMeetingActionItem(api, selectedMeeting.id, {
        description: actionItemForm.description,
        assigned_to: actionItemForm.assigned_to || undefined,
        due_date: actionItemForm.due_date || undefined
      });
      setActionItemForm({ description: '', assigned_to: '', due_date: '' });
      const updated = await getMeeting(api, selectedMeeting.id);
      setSelectedMeeting(updated.data);
    } catch (error) {
      console.error('Failed to add action item:', error);
    }
  };

  const handleUpdateActionStatus = async (actionId, newStatus) => {
    if (!selectedMeeting) return;
    try {
      await updateMeetingActionItem(api, selectedMeeting.id, actionId, { status: newStatus });
      const updated = await getMeeting(api, selectedMeeting.id);
      setSelectedMeeting(updated.data);
    } catch (error) {
      console.error('Failed to update action item:', error);
    }
  };

  const viewMeeting = async (meeting) => {
    try {
      const response = await getMeeting(api, meeting.id);
      setSelectedMeeting(response.data);
      setActiveTab('details');
      setShowViewDialog(true);
    } catch (error) {
      console.error('Failed to fetch meeting details:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_ar: '',
      meeting_type: 'safety_review',
      description: '',
      scheduled_date: '',
      scheduled_time: '09:00',
      location: '',
      duration_minutes: 60
    });
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch = meeting.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         meeting.meeting_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || meeting.status === statusFilter;
    const matchesType = typeFilter === 'all' || meeting.meeting_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: meetings.length,
    scheduled: meetings.filter(m => m.status === 'scheduled').length,
    inProgress: meetings.filter(m => m.status === 'in_progress').length,
    completed: meetings.filter(m => m.status === 'completed').length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMeetingTypeLabel = (type) => {
    const config = MEETING_TYPES.find(t => t.value === type);
    return i18n.language === 'ar' ? config?.labelAr : config?.label;
  };

  return (
    <div className="p-6 space-y-6" data-testid="meetings-page">
      {/* Header */}
      <div className={cn("flex flex-col sm:flex-row justify-between gap-4", isRTL && "sm:flex-row-reverse")}>
        <div>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {t('meetings.title', 'Safety Meetings')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('meetings.subtitle', 'Schedule and manage safety committee meetings')}
          </p>
        </div>
        {canManageMeetings && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="schedule-meeting-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('meetings.schedule', 'Schedule Meeting')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-slate-700 rounded-lg">
                <Calendar className="w-5 h-5 text-slate-300" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">{t('meetings.totalMeetings', 'Total Meetings')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-blue-900/50 rounded-lg">
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{stats.scheduled}</p>
                <p className="text-xs text-slate-400">{t('common.scheduled', 'Scheduled')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-amber-900/50 rounded-lg">
                <Play className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{stats.inProgress}</p>
                <p className="text-xs text-slate-400">{t('common.inProgress', 'In Progress')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-emerald-900/50 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">{stats.completed}</p>
                <p className="text-xs text-slate-400">{t('common.completed', 'Completed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className={cn("flex flex-col md:flex-row gap-4", isRTL && "md:flex-row-reverse")}>
            <div className="flex-1">
              <div className="relative">
                <Search className={cn("absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400", isRTL ? "right-3" : "left-3")} />
                <Input
                  placeholder={t('common.search', 'Search...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("bg-slate-900 border-slate-700 text-white", isRTL ? "pr-10" : "pl-10")}
                  data-testid="meetings-search"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700 text-white" data-testid="status-filter">
                  <SelectValue placeholder={t('common.status', 'Status')} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                  <SelectItem value="scheduled">{t('common.scheduled', 'Scheduled')}</SelectItem>
                  <SelectItem value="in_progress">{t('common.inProgress', 'In Progress')}</SelectItem>
                  <SelectItem value="completed">{t('common.completed', 'Completed')}</SelectItem>
                  <SelectItem value="cancelled">{t('common.cancelled', 'Cancelled')}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white" data-testid="type-filter">
                  <SelectValue placeholder={t('meetings.type', 'Meeting Type')} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
                  {MEETING_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {i18n.language === 'ar' ? type.labelAr : type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meetings List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      ) : filteredMeetings.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">{t('meetings.noMeetings', 'No meetings found')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredMeetings.map(meeting => {
            const statusConfig = STATUS_CONFIG[meeting.status];
            const StatusIcon = statusConfig?.icon || Calendar;
            
            return (
              <Card 
                key={meeting.id} 
                className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                onClick={() => viewMeeting(meeting)}
                data-testid={`meeting-card-${meeting.id}`}
              >
                <CardContent className="p-4">
                  <div className={cn("flex items-start justify-between gap-4", isRTL && "flex-row-reverse")}>
                    <div className="flex-1 space-y-2">
                      <div className={cn("flex items-center gap-2 flex-wrap", isRTL && "flex-row-reverse")}>
                        <Badge className={cn("text-xs", statusConfig?.color)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {i18n.language === 'ar' ? statusConfig?.labelAr : statusConfig?.label}
                        </Badge>
                        <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
                          {meeting.meeting_number}
                        </Badge>
                        <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                          {getMeetingTypeLabel(meeting.meeting_type)}
                        </Badge>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white">
                        {i18n.language === 'ar' && meeting.title_ar ? meeting.title_ar : meeting.title}
                      </h3>
                      
                      <div className={cn("flex items-center gap-4 text-sm text-slate-400 flex-wrap", isRTL && "flex-row-reverse")}>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <Calendar className="w-4 h-4" />
                          {formatDate(meeting.scheduled_date)}
                        </span>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <Clock className="w-4 h-4" />
                          {formatTime(meeting.scheduled_date)}
                        </span>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <MapPin className="w-4 h-4" />
                          {i18n.language === 'ar' && meeting.location_ar ? meeting.location_ar : meeting.location}
                        </span>
                        <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                          <Users className="w-4 h-4" />
                          {meeting.attendees?.length || 0} {t('meetings.attendees', 'attendees')}
                        </span>
                      </div>
                    </div>
                    
                    <div className={cn("flex items-center gap-2", isRTL && "flex-row-reverse")}>
                      {canManageMeetings && meeting.status === 'scheduled' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                            onClick={(e) => { e.stopPropagation(); handleStart(meeting); }}
                            data-testid={`start-meeting-${meeting.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            {t('meetings.start', 'Start')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-600 text-slate-400 hover:bg-slate-700"
                            onClick={(e) => { e.stopPropagation(); handleCancel(meeting); }}
                            data-testid={`cancel-meeting-${meeting.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <ChevronRight className={cn("w-5 h-5 text-slate-500", isRTL && "rotate-180")} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Meeting Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('meetings.schedule', 'Schedule Meeting')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('meetings.meetingTitle', 'Meeting Title')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder={t('meetings.titlePlaceholder', 'e.g., Monthly Safety Review Board')}
                className="bg-slate-800 border-slate-700"
                data-testid="meeting-title-input"
              />
            </div>
            
            <div>
              <Label>{t('meetings.type', 'Meeting Type')} *</Label>
              <Select 
                value={formData.meeting_type} 
                onValueChange={(value) => setFormData({...formData, meeting_type: value})}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="meeting-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {MEETING_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {i18n.language === 'ar' ? type.labelAr : type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common.date', 'Date')} *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="meeting-date-input"
                />
              </div>
              <div>
                <Label>{t('common.time', 'Time')} *</Label>
                <Input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="meeting-time-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('meetings.location', 'Location')} *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder={t('meetings.locationPlaceholder', 'e.g., Conference Room A')}
                  className="bg-slate-800 border-slate-700"
                  data-testid="meeting-location-input"
                />
              </div>
              <div>
                <Label>{t('meetings.duration', 'Duration (minutes)')}</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="meeting-duration-input"
                />
              </div>
            </div>
            
            <div>
              <Label>{t('common.description', 'Description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('meetings.descriptionPlaceholder', 'Meeting agenda and objectives...')}
                className="bg-slate-800 border-slate-700 min-h-[80px]"
                data-testid="meeting-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={!formData.title || !formData.scheduled_date || !formData.location || saving}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="create-meeting-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('meetings.schedule', 'Schedule Meeting')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Meeting Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <div className={cn("flex items-center justify-between", isRTL && "flex-row-reverse")}>
              <DialogTitle className="text-xl">
                {i18n.language === 'ar' && selectedMeeting?.title_ar ? selectedMeeting.title_ar : selectedMeeting?.title}
              </DialogTitle>
              {selectedMeeting && (
                <Badge className={STATUS_CONFIG[selectedMeeting.status]?.color}>
                  {i18n.language === 'ar' 
                    ? STATUS_CONFIG[selectedMeeting.status]?.labelAr 
                    : STATUS_CONFIG[selectedMeeting.status]?.label}
                </Badge>
              )}
            </div>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-800 w-full">
              <TabsTrigger value="details" className="flex-1 data-[state=active]:bg-slate-700">
                <Eye className="w-4 h-4 mr-2" />
                {t('common.details', 'Details')}
              </TabsTrigger>
              <TabsTrigger value="attendees" className="flex-1 data-[state=active]:bg-slate-700">
                <Users className="w-4 h-4 mr-2" />
                {t('meetings.attendees', 'Attendees')}
              </TabsTrigger>
              <TabsTrigger value="actions" className="flex-1 data-[state=active]:bg-slate-700">
                <ListTodo className="w-4 h-4 mr-2" />
                {t('meetings.actionItems', 'Action Items')}
              </TabsTrigger>
              {selectedMeeting?.status === 'completed' && (
                <TabsTrigger value="minutes" className="flex-1 data-[state=active]:bg-slate-700">
                  <FileText className="w-4 h-4 mr-2" />
                  {t('meetings.minutes', 'Minutes')}
                </TabsTrigger>
              )}
            </TabsList>
            
            <ScrollArea className="h-[400px] mt-4">
              <TabsContent value="details" className="space-y-4 m-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('meetings.meetingNumber', 'Meeting Number')}</p>
                    <p className="text-white font-medium">{selectedMeeting?.meeting_number}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('meetings.type', 'Type')}</p>
                    <p className="text-white">{getMeetingTypeLabel(selectedMeeting?.meeting_type)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('common.date', 'Date')}</p>
                    <p className="text-white">{formatDate(selectedMeeting?.scheduled_date)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('common.time', 'Time')}</p>
                    <p className="text-white">{formatTime(selectedMeeting?.scheduled_date)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('meetings.location', 'Location')}</p>
                    <p className="text-white">{selectedMeeting?.location}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('meetings.duration', 'Duration')}</p>
                    <p className="text-white">{selectedMeeting?.duration_minutes} {t('common.minutes', 'minutes')}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">{t('meetings.organizer', 'Organizer')}</p>
                    <p className="text-white">{selectedMeeting?.organizer_name}</p>
                  </div>
                </div>
                
                {selectedMeeting?.description && (
                  <div className="space-y-1 pt-4 border-t border-slate-700">
                    <p className="text-xs text-slate-500">{t('common.description', 'Description')}</p>
                    <p className="text-slate-300">{selectedMeeting.description}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                {canManageMeetings && selectedMeeting?.status === 'in_progress' && (
                  <div className="pt-4 border-t border-slate-700">
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        setMinutesText(selectedMeeting.minutes || '');
                        setShowMinutesDialog(true);
                      }}
                      data-testid="complete-meeting-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t('meetings.completeMeeting', 'Complete Meeting')}
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="attendees" className="space-y-4 m-0">
                {canManageMeetings && selectedMeeting?.status !== 'completed' && selectedMeeting?.status !== 'cancelled' && (
                  <div className="flex gap-2">
                    <Select onValueChange={handleAddAttendee}>
                      <SelectTrigger className="flex-1 bg-slate-800 border-slate-700" data-testid="add-attendee-select">
                        <SelectValue placeholder={t('meetings.addAttendee', 'Add Attendee')} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {users
                          .filter(u => !selectedMeeting?.attendees?.includes(u.id))
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} ({u.role})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="space-y-2">
                  {selectedMeeting?.attendee_names?.length > 0 ? (
                    selectedMeeting.attendee_names.map(attendee => (
                      <div 
                        key={attendee.id} 
                        className={cn(
                          "flex items-center justify-between p-3 bg-slate-800 rounded-lg",
                          isRTL && "flex-row-reverse"
                        )}
                      >
                        <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-300" />
                          </div>
                          <span className="text-white">{attendee.name}</span>
                        </div>
                        {canManageMeetings && selectedMeeting?.status !== 'completed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-red-400"
                            onClick={() => handleRemoveAttendee(attendee.id)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('meetings.noAttendees', 'No attendees recorded yet')}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="actions" className="space-y-4 m-0">
                {canManageMeetings && selectedMeeting?.status !== 'cancelled' && (
                  <div className="space-y-3 p-4 bg-slate-800/50 rounded-lg">
                    <Input
                      placeholder={t('meetings.actionDescription', 'Action item description...')}
                      value={actionItemForm.description}
                      onChange={(e) => setActionItemForm({...actionItemForm, description: e.target.value})}
                      className="bg-slate-900 border-slate-700"
                      data-testid="action-item-description"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Select 
                        value={actionItemForm.assigned_to} 
                        onValueChange={(value) => setActionItemForm({...actionItemForm, assigned_to: value})}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700" data-testid="action-item-assignee">
                          <SelectValue placeholder={t('meetings.assignTo', 'Assign to...')} />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="date"
                        value={actionItemForm.due_date}
                        onChange={(e) => setActionItemForm({...actionItemForm, due_date: e.target.value})}
                        className="bg-slate-900 border-slate-700"
                        data-testid="action-item-due-date"
                      />
                    </div>
                    <Button 
                      onClick={handleAddActionItem}
                      disabled={!actionItemForm.description}
                      className="w-full bg-sky-600 hover:bg-sky-700"
                      data-testid="add-action-item-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('meetings.addActionItem', 'Add Action Item')}
                    </Button>
                  </div>
                )}
                
                <div className="space-y-2">
                  {selectedMeeting?.action_items?.length > 0 ? (
                    selectedMeeting.action_items.map(action => (
                      <div 
                        key={action.id} 
                        className={cn(
                          "p-3 bg-slate-800 rounded-lg space-y-2",
                          action.status === 'completed' && "opacity-60"
                        )}
                      >
                        <div className={cn("flex items-start justify-between", isRTL && "flex-row-reverse")}>
                          <div className="flex-1">
                            <p className={cn(
                              "text-white",
                              action.status === 'completed' && "line-through"
                            )}>
                              {action.description}
                            </p>
                            <div className={cn("flex items-center gap-3 mt-1 text-xs text-slate-400", isRTL && "flex-row-reverse")}>
                              {action.assigned_to_name && (
                                <span className={cn("flex items-center gap-1", isRTL && "flex-row-reverse")}>
                                  <User className="w-3 h-3" />
                                  {action.assigned_to_name}
                                </span>
                              )}
                              {action.due_date && (
                                <span className={cn(
                                  "flex items-center gap-1",
                                  new Date(action.due_date) < new Date() && action.status !== 'completed' && "text-red-400",
                                  isRTL && "flex-row-reverse"
                                )}>
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(action.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                          {canManageMeetings && action.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-400 hover:text-emerald-300"
                              onClick={() => handleUpdateActionStatus(action.id, 'completed')}
                              data-testid={`complete-action-${action.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <Badge 
                          className={cn(
                            "text-xs",
                            action.status === 'open' && "bg-blue-600",
                            action.status === 'in_progress' && "bg-amber-600",
                            action.status === 'completed' && "bg-emerald-600"
                          )}
                        >
                          {action.status === 'open' && t('common.open', 'Open')}
                          {action.status === 'in_progress' && t('common.inProgress', 'In Progress')}
                          {action.status === 'completed' && t('common.completed', 'Completed')}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>{t('meetings.noActionItems', 'No action items recorded')}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="minutes" className="m-0">
                {selectedMeeting?.minutes ? (
                  <div className="p-4 bg-slate-800 rounded-lg">
                    <p className="text-slate-300 whitespace-pre-wrap">{selectedMeeting.minutes}</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('meetings.noMinutes', 'No minutes recorded')}</p>
                  </div>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Complete Meeting / Minutes Dialog */}
      <Dialog open={showMinutesDialog} onOpenChange={setShowMinutesDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('meetings.completeMeeting', 'Complete Meeting')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-900/20 border border-amber-700 rounded-lg">
              <div className={cn("flex items-center gap-2", isRTL && "flex-row-reverse")}>
                <AlertCircle className="w-4 h-4 text-amber-400" />
                <p className="text-amber-200 text-sm">
                  {t('meetings.completeWarning', 'This will mark the meeting as completed. You can add meeting minutes before finalizing.')}
                </p>
              </div>
            </div>
            
            <div>
              <Label>{t('meetings.minutes', 'Meeting Minutes')}</Label>
              <Textarea
                value={minutesText}
                onChange={(e) => setMinutesText(e.target.value)}
                placeholder={t('meetings.minutesPlaceholder', 'Enter meeting minutes, key decisions, and discussion points...')}
                className="bg-slate-800 border-slate-700 min-h-[200px]"
                data-testid="meeting-minutes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMinutesDialog(false)} className="border-slate-600">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleComplete}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-complete-meeting"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('meetings.completeMeeting', 'Complete Meeting')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
